import BoldSmallText from '@/components/BoldSmallText';
import CustomLayoutGeneralSettings from '@/components/CustomLayoutGeneralSettings';
import UpgradeTextWithTouchPoint from '@/components/UpgradeTextWithTouchPoint';
import { booleanToNumber, numberToBoolean } from '@/helpers';
import useUserPlan from '@/hooks/useUserPlan';
import settingsSlice, { dataSettingsBackupSelector, dataSettingsSelector } from '@/redux/slice/settings.slice';
import { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RegularText from '../RegularText';
import Switch from '../Switch';
import CustomCard from '../customCard';
import { useMediaQuery } from 'react-responsive';
import { BREAKPOINT } from '@/constants/enum';

const AutoDetect = () => {
  const dispatch = useDispatch();
  const dataSettings = useSelector(dataSettingsSelector);
  const dataSettingsBackup = useSelector(dataSettingsBackupSelector);
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.XS });
  const { isPlanLimited } = useUserPlan();

  const getCustomerZipcodeStatus = useMemo(() => {
    return numberToBoolean(dataSettings.get_customer_zipcode);
  }, [dataSettings.get_customer_zipcode]);

  const getCustomerZipcodeStatusBackup = useMemo(() => {
    return numberToBoolean(dataSettingsBackup.get_customer_zipcode);
  }, [dataSettingsBackup.get_customer_zipcode]);

  return (
    <div className="mt-8">
      <CustomLayoutGeneralSettings
        notUseCard
        title="Geolocation"
        description={<p>Used for rules with zipcode or country condition</p>}
      >
        <CustomCard
          title={
            <span className={`d-flex align-center ${isPlanLimited ? 'text--gray' : ''}`} style={{ fontSize: '13px' }}>
              Auto-detect zipcode/country
            </span>
          }
          titleContentRight={
            !getCustomerZipcodeStatusBackup && isPlanLimited ? (
              !isMobile ? (
                <div className="min-w-fit">
                  <UpgradeTextWithTouchPoint isVisible={isPlanLimited} text="Pro" />
                </div>
              ) : undefined
            ) : (
              <Switch
                isActive={getCustomerZipcodeStatus}
                isLoading={!getCustomerZipcodeStatus && isPlanLimited}
                onSwitch={(value) =>
                  dispatch(
                    settingsSlice.actions.handleDataSettings({
                      ...dataSettings,
                      get_customer_zipcode: booleanToNumber(value),
                      isHiddenCountryRulesNotDetected: !value ? false : dataSettings.isHiddenCountryRulesNotDetected,
                    }),
                  )
                }
              />
            )
          }
        >
          <div className="mt-8 mb-8">
            <RegularText className={`${isPlanLimited ? 'text--gray' : ''}`}>
              Automatically detect zipcode/country of your site visitors.
            </RegularText>
            {isMobile && !getCustomerZipcodeStatusBackup && (
              <div>
                <UpgradeTextWithTouchPoint isVisible={isPlanLimited} text="Pro" />
              </div>
            )}
          </div>
          <div className="d-flex align-center justify-between pt-8 mb-8">
            <BoldSmallText className={`${isPlanLimited ? 'text--gray' : ''}`}>
              Only display rules for the detected location
            </BoldSmallText>
            {isPlanLimited ? (
              !isMobile && (
                <div className="min-w-fit">
                  <UpgradeTextWithTouchPoint isVisible={isPlanLimited} text="Pro" />
                </div>
              )
            ) : (
              <Switch
                disabled={!dataSettings.get_customer_zipcode}
                isActive={!!dataSettings.isHiddenCountryRulesNotDetected}
                isLoading={isPlanLimited}
                onSwitch={(value) =>
                  dataSettings.get_customer_zipcode &&
                  dispatch(
                    settingsSlice.actions.handleDataSettings({
                      ...dataSettings,
                      isHiddenCountryRulesNotDetected: value,
                    }),
                  )
                }
                customTooltipWhenDisabled="Need to turn on Auto-detect zipcode/country"
              />
            )}
          </div>
          <RegularText className={`${isPlanLimited ? 'text--gray' : ''}`}>
            This means that only the rules with location conditions matching the visitor’s location will be shown, while others
            will remain hidden. Rules without specific location conditions are still shown.
          </RegularText>
          {isMobile && (
            <div>
              <UpgradeTextWithTouchPoint isVisible={isPlanLimited} text="Pro" />
            </div>
          )}
        </CustomCard>
      </CustomLayoutGeneralSettings>
    </div>
  );
};

export default memo(AutoDetect);
